import { WEAVR_DATE_OF_BIRTH_FORMAT } from "@finway-group/shared/lib/consts/weavr"
import { Employee } from "@finway-group/shared/lib/models"
import omit from "lodash/omit"
import { type Moment } from "moment"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { useActiveCompanyQuery } from "~Shared/queries/company.queries"
import { invalidateEmployees, useEmployee, useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { SessionStorageService } from "~Shared/services/sessionstorage.service"
import { type ThunkDispatchResult } from "~Shared/store"
import { updateEmployee } from "~Shared/store/actions/employee/employeeActions"
import { isDateOfBirthRequired, shouldHoldAtDateOfBirthPage } from "~Shared/utils/employee.utils"

export const useSaveDateOfBirth = () => {
    const dispatch: ThunkDispatchResult = useDispatch()
    const [isSaving, setIsSaving] = useState(false)

    const saveDateOfBirth = async (employee: Employee, dateOfBirth: Moment) => {
        try {
            setIsSaving(true)
            const profile = omit(employee, ["companyProfiles", "activeCompanyProfile"])
            const updatedEmployee = new Employee({ ...profile, dateOfBirth: dateOfBirth.format(WEAVR_DATE_OF_BIRTH_FORMAT) })
            await dispatch(updateEmployee(employee.id, updatedEmployee))
            await invalidateEmployees()
        } finally {
            setIsSaving(false)
        }
    }

    return { saveDateOfBirth, isSaving }
}

export type DateOfBirthGateDecision = "undecided" | "hold" | "allow"

/**
 * Inputs come from queries, not the store: `DataFetcher` sits inside the subtree this gate replaces, so
 * waiting on the store unmounts the fetcher that would settle it — a deadlock on a cold store, a refetch loop on a warm one.
 */
export const useDateOfBirthGateDecision = (): DateOfBirthGateDecision => {
    const { userId, isFinwayAdmin } = useAuth()
    const { isLoading: isProfileLoading } = useEmployee(userId)
    const { data: company, isLoading: isCompanyLoading } = useActiveCompanyQuery()
    const employee = useLoggedInEmployeeProfile()

    const isSettled = !userId || (!isProfileLoading && !isCompanyLoading)
    const isDateOfBirthMissing = isDateOfBirthRequired({ employee, isCardProgramEnabled: company?.cardsEnabled, isFinwayAdmin })

    useEffect(() => {
        if (isSettled && !isDateOfBirthMissing) {
            SessionStorageService.markAppEntered()
        }
    }, [isSettled, isDateOfBirthMissing])

    if (!isSettled) {
        return "undecided"
    }

    return shouldHoldAtDateOfBirthPage({ isDateOfBirthMissing, hasEnteredApp: SessionStorageService.hasEnteredApp() }) ? "hold" : "allow"
}
